"use client";

import type { Dayjs } from "dayjs";
import Link from "next/link";
import dayjs from "@punchup/utils/dayjs";
import { useShowRowContext } from "@/components/shows/ShowRowContext";

// The tour list page omits the year for shows in the current calendar year, but
// includes it once a show falls in a different year, so a future-year date (e.g.
// "October 23") is no longer misread as this year (PUN-4592).
function isDifferentYear(date: Dayjs): boolean {
  return date.year() !== dayjs().year();
}

export function getShowDate(showDate: Dayjs): string {
  return showDate.format(isDifferentYear(showDate) ? "MMMM D, YYYY" : "MMMM D");
}

export function getShowDateRange(
  earliestShowDate: Dayjs,
  latestShowDate: Dayjs
): string {
  // Append the year once the range reaches beyond the current calendar year,
  // keyed on the latest endpoint so a range crossing New Year (e.g. Dec 30 to
  // Jan 2) surfaces the future year instead of reading as a past date (PUN-4592).
  const yearSuffix = isDifferentYear(latestShowDate)
    ? `, ${latestShowDate.year()}`
    : "";

  // if the earliest and latest show dates are the same calendar day, show "June 10"
  // (compare the full day, not just the day-of-month, so a Jun 10 -> Jul 10 range
  // is not mistaken for a single date).
  if (earliestShowDate.isSame(latestShowDate, "day")) {
    return `${earliestShowDate.format("MMMM")} ${earliestShowDate.format("D")}${yearSuffix}`;
  }

  const sameMonth = earliestShowDate.month() === latestShowDate.month();
  // if the earliest and latest show dates are the same month, show "June 10 to 12"
  if (sameMonth) {
    const consecutiveDays = earliestShowDate
      .add(1, "day")
      .isSame(latestShowDate, "day");
    // if they're two consective days, show "June 10 & 11"
    if (consecutiveDays) {
      return `${earliestShowDate.format("MMMM")} ${earliestShowDate.format("D")} to ${latestShowDate.format("D")}${yearSuffix}`;
    }

    // otherwise, they must be more than two days apart, so show "June 10 to 12"
    return `${earliestShowDate.format("MMMM")} ${earliestShowDate.format("D")} to ${latestShowDate.format("D")}${yearSuffix}`;
  }

  // if the earliest and latest show dates are in different months, show "June 10 to July 12"
  return `${earliestShowDate.format("MMM")} ${earliestShowDate.format("D")} to ${latestShowDate.format("MMM")} ${latestShowDate.format("D")}${yearSuffix}`;
}

export function getDayLabel(date: Dayjs): string {
  try {
    const today = dayjs();
    return today && today.isSame && today.isSame(date, "day")
      ? "Today"
      : date.format("ddd");
  } catch {
    return date.format("ddd");
  }
}

type ShowRowDateFormat = "date-range" | "day-time" | "count-or-day-time";

interface ShowRowDateProps {
  format?: ShowRowDateFormat;
}

export function ShowRowDate({ format = "date-range" }: ShowRowDateProps) {
  const { shows, earliestShowDate, latestShowDate } = useShowRowContext();
  const show = shows[0];
  const multipleShows = shows.length > 1;

  if (format === "day-time") {
    return (
      <Link href={`/e/${show.id}`}>
        {getDayLabel(show.datetime)}
        {show.datetime.format(", MMM D | h:mma")}
      </Link>
    );
  }

  if (format === "count-or-day-time") {
    if (multipleShows) {
      // note: no Link for multiple locations yet, we don't have show/event pages for multiple events yet.
      return <>{shows.length} shows</>;
    }
    return (
      <Link href={`/e/${show.id}`}>
        {getDayLabel(show.datetime)}
        {show.datetime.format(", MMM D | h:mma")}
      </Link>
    );
  }

  // date-range (default)
  if (multipleShows) {
    // note: no Link for multiple locations yet, we don't have show/event pages for multiple events yet.
    return (
      <p className="text-lg font-bold text-primary-blue leading-tight line-clamp-1">
        {getShowDateRange(earliestShowDate, latestShowDate)}
      </p>
    );
  }

  return (
    <Link href={`/e/${show.id}`}>
      <p className="text-lg font-bold text-primary-blue leading-tight line-clamp-1">
        {getShowDate(show.datetime)}
      </p>
    </Link>
  );
}
